import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { useDarkMode } from '../context/ThemeContext';
import {
  SquaresFour, Ticket, Plus, Users, Gear, Tray, Tag, FlowArrow, Package, Envelope,
  Eye, EyeSlash, X, Key,
} from '@phosphor-icons/react';
import { EntityChip } from './redesign/EntityChip';
import { EntitySelector } from './redesign/EntitySelector';
import { useEntityFilter } from '../context/EntityFilterContext';
import { Button } from './redesign/ui';
import LayoutView from './Layout.view';
import { slugsForDepartment } from '../constants/ticketOptions';

// Optional visibleWhen predicate on a NAV_DEF entry NARROWS visibility below
// what canAccess allowed (department/capability/etc.). Absent = unchanged
// behavior. Predicates must be null-safe — filter runs mid-render, user can
// briefly be undefined.
//
// canSeeSampleCollections mirrors the backend endpoint's auth gate exactly
// (backend/src/routes/sampleCollections.js requireCollectionAccess):
// super_admin/entity_manager OR any user in the Logistics dept family.
const canSeeSampleCollections = (user) => {
  if (!user) return false;
  if (['super_admin', 'entity_manager'].includes(user.role)) return true;
  return slugsForDepartment(user.department).includes('logistics');
};

// page key maps to the permissions matrix; every item uses a hardcoded
// English label. Icons are phosphor equivalents of the pre-redesign lucide set.
// Optional visibleWhen: (user)=>boolean narrows visibility further (see above).
const NAV_DEFS = [
  { path: '/dashboard',   page: 'dashboard',      label: 'Dashboard',      icon: SquaresFour },
  { path: '/tickets',     page: 'tickets',        label: 'Tickets',        icon: Ticket      },
  { path: '/pending',             page: 'pending_action',    label: 'Pending Action',    icon: Tray, badge: 'pendingAction' },
  { path: '/sample-collections',  page: 'sample_collections', label: 'Sample Collections', icon: Package, visibleWhen: canSeeSampleCollections },
  { path: '/tickets/new',         page: 'new_ticket',         label: 'New Ticket',        icon: Plus        },
  { path: '/users',       page: 'users',          label: 'Users',          icon: Users       },
  { path: '/admin',           page: 'admin',           label: 'Admin Panel',     icon: Gear        },
  { path: '/email-monitor',   page: 'email_monitor',   label: 'Email Monitor',   icon: Envelope    },
  { path: '/offers',          page: 'offers',          label: 'Offers & Promos', icon: Tag         },
  { path: '/flows',       page: 'flows',          label: 'Flows',          icon: FlowArrow   },
];

// Human-readable integration names for the shell TestModeBanner enumeration.
const INTEGRATION_NAMES = {
  abraaj_live_mode:            'Abraaj Water',
  lofat_live_mode:             'Lofat Lifestyle',
  instagram_abraaj_live_mode:  'Instagram · Abraaj',
};

// Shared token-styled input class.
const INPUT = 'w-full rounded-field border border-paper-border2 bg-paper px-3 py-2 pr-10 text-sm text-ink outline-none focus:border-ink';
const INPUT_ERR = 'w-full rounded-field border border-status-escalated bg-paper px-3 py-2 pr-10 text-sm text-ink outline-none focus:border-ink';

// Neutral "All Entities" pill for super_admin (matches Users' entity cell).
function AllEntitiesPill() {
  return (
    <span className="inline-flex rounded-chip bg-paper-muted px-2.5 py-1 text-[11px] font-semibold text-ink-muted">
      All Entities
    </span>
  );
}

// Self-serve password change modal — smart-sibling to <LayoutView>. Kept
// inline in this file (same pattern used by Users' UserModal, Flows'
// SlidePanel, etc.). All state + the axios call live here; the view knows
// nothing about it.
function ChangePasswordModal({ open, onClose }) {
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw]     = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [serverError, setServerError] = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const currentRef = useRef(null);

  // Autofocus current-password on open; clear all state on close.
  useEffect(() => {
    if (open) {
      setCurrent(''); setNewPw(''); setConfirm('');
      setShowCurrent(false); setShowNew(false); setShowConfirm(false);
      setServerError('');
      // Defer focus until the modal has mounted the input.
      setTimeout(() => currentRef.current?.focus(), 30);
    }
  }, [open]);

  // Escape closes the modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Client-side validation — matches the server rules (kept in sync).
  const tooShort = newPw.length > 0 && newPw.length < 8;
  const sameAsCurrent = current.length > 0 && newPw.length > 0 && newPw === current;
  const mismatch = confirm.length > 0 && confirm !== newPw;
  const canSubmit =
    !submitting &&
    current.length > 0 &&
    newPw.length >= 8 &&
    newPw !== current &&
    confirm === newPw;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setServerError('');
    try {
      await axios.post(
        '/api/auth/change-password',
        { current_password: current, new_password: newPw },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Password updated.');
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      const data   = err?.response?.data || {};
      // 401 with our sentinel code === "current password wrong". A 401
      // WITHOUT the code came from the auth middleware (session gone).
      if (status === 401 && data.code === 'WRONG_CURRENT_PASSWORD') {
        setServerError('Current password is incorrect. Please try again.');
        setTimeout(() => currentRef.current?.focus(), 30);
      } else if (status === 401) {
        toast.error('Session expired. Please sign in again.');
        onClose();
        navigate('/login');
      } else if (status === 400) {
        setServerError(data.error || 'Password does not meet requirements.');
      } else if (typeof status === 'number') {
        setServerError('Something went wrong. Please try again.');
      } else {
        setServerError('Network error — please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Reveal button (Eye/EyeSlash) — absolute inside the input's relative wrapper.
  const RevealBtn = ({ visible, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint transition hover:text-ink"
      title={visible ? 'Hide password' : 'Show password'}>
      {visible ? <EyeSlash size={16} /> : <Eye size={16} />}
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-sm"
      onClick={onClose}>
      <div
        className="w-full max-w-md rounded-card border border-paper-border bg-paper-panel shadow-soft"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-paper-border px-6 py-4">
          <h2 className="flex items-center gap-2 font-serif text-base text-ink">
            <Key size={16} />Change password
          </h2>
          <button onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-field text-ink-muted hover:bg-paper-muted">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
          {/* Server error banner */}
          {serverError && (
            <div className="rounded-field border border-status-escalated/30 bg-status-escalated/8 px-3 py-2 text-sm text-status-escalated">
              {serverError}
            </div>
          )}

          {/* Current */}
          <div>
            <label className="mb-1 block text-xs text-ink-faint">Current password</label>
            <div className="relative">
              <input
                ref={currentRef}
                type={showCurrent ? 'text' : 'password'}
                value={current}
                onChange={(e) => { setCurrent(e.target.value); setServerError(''); }}
                autoComplete="current-password"
                required
                className={serverError ? INPUT_ERR : INPUT}
              />
              <RevealBtn visible={showCurrent} onClick={() => setShowCurrent((v) => !v)} />
            </div>
          </div>

          {/* New */}
          <div>
            <label className="mb-1 block text-xs text-ink-faint">New password</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                autoComplete="new-password"
                required
                className={(tooShort || sameAsCurrent) ? INPUT_ERR : INPUT}
              />
              <RevealBtn visible={showNew} onClick={() => setShowNew((v) => !v)} />
            </div>
            {tooShort && (
              <p className="mt-1 text-xs text-status-escalated">New password must be at least 8 characters.</p>
            )}
            {!tooShort && sameAsCurrent && (
              <p className="mt-1 text-xs text-status-escalated">New password must differ from your current one.</p>
            )}
            {!tooShort && !sameAsCurrent && newPw.length === 0 && (
              <p className="mt-1 text-xs text-ink-faint">At least 8 characters. Must differ from your current password.</p>
            )}
          </div>

          {/* Confirm */}
          <div>
            <label className="mb-1 block text-xs text-ink-faint">Confirm new password</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
                className={mismatch ? INPUT_ERR : INPUT}
              />
              <RevealBtn visible={showConfirm} onClick={() => setShowConfirm((v) => !v)} />
            </div>
            {mismatch && (
              <p className="mt-1 text-xs text-status-escalated">Passwords do not match.</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={!canSubmit}>
              {submitting ? 'Updating…' : 'Update password'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout }             = useAuth();
  const { canAccess }                = usePermissions();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const location                     = useLocation();
  const navigate                     = useNavigate();

  const [sidebarOpen, setSidebarOpen]       = useState(false);
  const [modeData, setModeData]             = useState(null);
  const [pendingCount, setPendingCount]     = useState(0);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [changePwOpen, setChangePwOpen]     = useState(false);
  const avatarMenuRef = useRef(null);

  // Mode-data fetch — used to enumerate integrations in test mode for the
  // shell banner. Response returns both {_live_mode} and {_test_mode} flags;
  // we read {_live_mode} for consistency with AdminPanel and derive test
  // state by negation.
  useEffect(() => {
    fetch('/api/settings/mode')
      .then((r) => r.json())
      .then(setModeData)
      .catch(() => {});
  }, []);

  // Actionable-count badge for the Pending Action nav item — refreshed on
  // every route change (cheap endpoint), fail-soft to 0.
  useEffect(() => {
    if (!user) return;
    fetch('/api/tickets/pending-action', {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setPendingCount(data?.actionableCount || 0))
      .catch(() => setPendingCount(0));
  }, [user, location.pathname]);

  // Close avatar menu on outside click.
  useEffect(() => {
    if (!avatarMenuOpen) return;
    const onClick = (e) => {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(e.target)) {
        setAvatarMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [avatarMenuOpen]);

  // Close avatar menu on Escape.
  useEffect(() => {
    if (!avatarMenuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setAvatarMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [avatarMenuOpen]);

  const handleLogout = () => {
    setAvatarMenuOpen(false);
    logout();
    navigate('/login');
  };

  const handleChangePassword = () => {
    setAvatarMenuOpen(false);
    setChangePwOpen(true);
  };

  const visibleNav = NAV_DEFS.filter((item) =>
    canAccess(item.page, user) && (!item.visibleWhen || item.visibleWhen(user))
  );

  // Active-path detection with special /tickets case — every path starting
  // with /tickets is Tickets except /tickets/new which is a distinct nav item.
  const activeNavPath = (() => {
    const exact = visibleNav.find((item) => location.pathname === item.path);
    if (exact) return exact.path;
    if (location.pathname.startsWith('/tickets') && location.pathname !== '/tickets/new') {
      return '/tickets';
    }
    return null;
  })();

  // Entity chip / selector.
  //   - Multi-entity user (super_admin, or entities[] length > 1): interactive
  //     dropdown driven by EntityFilterContext. Selecting an entity filters
  //     the whole app; "All Entities" restores the combined view.
  //   - Single-entity user: static entity-tinted chip (no choice to offer).
  //   - No entity at all: nothing rendered.
  const { showSelector: showEntitySelector } = useEntityFilter();
  const entityChip = showEntitySelector
    ? <EntitySelector />
    : (user?.role === 'super_admin'
        ? <AllEntitiesPill />
        : (user?.entity_slug ? <EntityChip entitySlug={user.entity_slug} /> : null));

  // Integrations currently in test mode — derived from the /_live_mode fields.
  const integrationsInTestMode = modeData
    ? Object.entries(INTEGRATION_NAMES)
        .filter(([key]) => modeData[key] === false)
        .map(([, label]) => label)
    : [];

  return (
    <>
      <LayoutView
        user={user}
        visibleNav={visibleNav}
        activeNavPath={activeNavPath}
        pendingCount={pendingCount}
        entityChip={entityChip}
        integrationsInTestMode={integrationsInTestMode}
        showBannerSettingsLink={user?.role === 'super_admin'}
        darkMode={darkMode}
        sidebarOpen={sidebarOpen}
        onOpenSidebar={() => setSidebarOpen(true)}
        onCloseSidebar={() => setSidebarOpen(false)}
        onNavigate={() => setSidebarOpen(false)}
        onLogout={handleLogout}
        onToggleDarkMode={toggleDarkMode}
        onGoToSettings={() => navigate('/admin')}
        avatarMenuOpen={avatarMenuOpen}
        avatarMenuRef={avatarMenuRef}
        onToggleAvatarMenu={() => setAvatarMenuOpen((v) => !v)}
        onChangePassword={handleChangePassword}>
        {children}
      </LayoutView>
      <ChangePasswordModal open={changePwOpen} onClose={() => setChangePwOpen(false)} />
    </>
  );
}
